import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as Cesium from 'cesium';
import { ChevronDown, Map, Satellite, LocateFixed, Locate, Plus, Minus, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, RefreshCcw, MoveVertical, Search, Crosshair, Mountain, SlidersHorizontal } from 'lucide-react';
import { calculateTerrainAwareAltitude } from '../utils/areaUtils';
import { metersToFeet } from '../utils/units';
import { applyTerrainModeToAllVectorLayers } from '../utils/vectorLayerTerrainSync';
import { useDataCoverageOverlay } from '../hooks/useDataCoverageOverlay';
import { useUnitPreferences } from '../hooks/useUnitPreferences';
import { CoverageControlUI } from './CoverageControlUI';
import { CoverageHoverTooltip } from './CoverageHoverTooltip';
import { MorphPanel } from './shared/MorphPanel';
import { eventBus, useEventSubscribe } from '../events';
const clamp = (value, min, max) => {
    return Math.max(min, Math.min(max, value));
};
const formatNumber = (num) => {
    if (num < 10)
        return num.toFixed(2);
    if (num < 100)
        return num.toFixed(1);
    return Math.round(num).toLocaleString();
};
const formatMetricDistance = (meters) => {
    if (meters > 5000) {
        const km = meters / 1000;
        return { text: `${formatNumber(km)} km`, unit: 'km' };
    }
    return { text: `${formatNumber(meters)} m`, unit: 'm' };
};
const formatImperialDistance = (feet) => {
    if (feet > 5280) {
        const mi = feet / 5280;
        return { text: `${formatNumber(mi)} mi`, unit: 'mi' };
    }
    return { text: `${formatNumber(feet)} ft`, unit: 'ft' };
};
// Thin labelled divider that introduces a group of controls (e.g. "Map display",
// "Basemap"). Matches the small uppercase label treatment used elsewhere in the
// panel.
const SectionDivider = ({ label }) => (_jsxs("div", { className: "flex items-center gap-2 mt-1 mb-0.5", children: [_jsx("div", { className: "flex-1 h-px", style: { backgroundColor: 'var(--pewter)' } }), _jsx("span", { className: "text-[10px] uppercase tracking-wide", style: { color: 'var(--dew)' }, children: label }), _jsx("div", { className: "flex-1 h-px", style: { backgroundColor: 'var(--pewter)' } })] }));
// Smooth press-and-hold camera control. While held, a requestAnimationFrame
// loop calls onHold(dt) each frame so motion glides like a key-hold instead of
// jumping in discrete steps (the per-frame distance is speed * dt, applied by
// the caller's movers). A quick tap still nudges via an initial impulse. One-shot
// buttons (recenter, restore-to-top-down) pass onAction instead of onHold.
// Defined at module scope so the live altitude re-renders don't remount it and
// cancel an in-progress hold.
const TAP_IMPULSE_S = 0.18; // seconds of motion applied on a tap / key press
const ControlButton = ({ onAction, onHold, title, disabled = false, children, round = false }) => {
    const rafRef = useRef(null);
    const lastRef = useRef(0);
    const stop = useCallback(() => {
        if (rafRef.current !== null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
    }, []);
    useEffect(() => stop, [stop]);
    const begin = useCallback((e) => {
        if (disabled || !onHold)
            return;
        e.preventDefault();
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        }
        catch (_e) { }
        onHold(TAP_IMPULSE_S); // immediate nudge so a quick tap still registers
        lastRef.current = performance.now();
        const tick = (now) => {
            const dt = Math.min(0.05, (now - lastRef.current) / 1000);
            lastRef.current = now;
            onHold(dt);
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
    }, [disabled, onHold]);
    const interaction = onHold
        ? {
            onPointerDown: begin,
            onPointerUp: stop,
            onPointerCancel: stop,
            onLostPointerCapture: stop,
            onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onHold(TAP_IMPULSE_S);
                }
            },
        }
        : { onClick: onAction };
    return (_jsxs("button", { type: "button", disabled: disabled, "aria-label": title, ...interaction, className: `relative group ${round ? 'rounded-full w-12 h-12 md:w-9 md:h-9' : 'p-2 rounded'} border transition-all flex items-center justify-center select-none touch-manipulation
        bg-[var(--charcoal)] border-[var(--pewter)] text-[var(--sand)] pointer-events-auto
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[var(--dew)] hover:text-[var(--forest)] hover:shadow-md active:scale-95 active:ring-2 active:ring-blue-400'}
      `, children: [children, _jsx("div", { className: "absolute -top-7 left-1/2 -translate-x-1/2 px-2 py-1 rounded text-[10px] pointer-events-none opacity-0 group-hover:opacity-100 transition", style: { backgroundColor: 'rgba(0,0,0,0.75)', color: '#fff' }, children: title })] }));
};
// Continuous-motion speeds. Pan/zoom scale with camera height so the glide feels
// the same at any altitude; tilt is a fixed angular rate.
const PAN_PER_SEC = 0.9; // fraction of camera height per second
const ZOOM_PER_SEC = 0.8; // fraction of camera height per second
const TILT_RAD_PER_SEC = Cesium.Math.toRadians(45);
export const MapControlPanel = ({ viewer, currentBasemap, onChangeBasemap, selectedProject }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeTab, setActiveTab] = useState('basemap');
    // Map controls state
    const [altitudeMeters, setAltitudeMeters] = useState(0);
    const { system, setSystem } = useUnitPreferences();
    // 3D terrain toggle. Off by default — swaps Cesium World Terrain in/out
    // and enables depthTestAgainstTerrain so on-ground entities clamp to
    // the elevation surface instead of bleeding through hills.
    const [terrainEnabled, setTerrainEnabled] = useState(false);
    const [terrainLoading, setTerrainLoading] = useState(false);
    // Data-coverage overlay state is owned here (always mounted while the panel is)
    // so the overlay survives collapsing the panel / switching tabs. The hook does
    // the Cesium work; CoverageControlUI is the toggle + slider, and the hover
    // tooltip is rendered at panel root so it shows even when collapsed.
    const [coverageVisible, setCoverageVisible] = useState(false);
    const [coverageOpacity, setCoverageOpacity] = useState(0.2);
    const [coverageHover, setCoverageHover] = useState(null);
    useDataCoverageOverlay(viewer, {
        visible: coverageVisible,
        opacity: coverageOpacity,
        onHover: setCoverageHover,
    });
    const onToggleTerrain = useCallback(async () => {
        if (!viewer || viewer.isDestroyed() || terrainLoading)
            return;
        if (terrainEnabled) {
            viewer.terrainProvider = new Cesium.EllipsoidTerrainProvider();
            viewer.scene.globe.depthTestAgainstTerrain = false;
            applyTerrainModeToAllVectorLayers(viewer, false);
            setTerrainEnabled(false);
            return;
        }
        setTerrainLoading(true);
        try {
            const terrain = await Cesium.createWorldTerrainAsync();
            if (viewer.isDestroyed())
                return;
            viewer.terrainProvider = terrain;
            viewer.scene.globe.depthTestAgainstTerrain = true;
            applyTerrainModeToAllVectorLayers(viewer, true);
            setTerrainEnabled(true);
        }
        catch (e) {
            console.error('Failed to load Cesium World Terrain', e);
        }
        finally {
            setTerrainLoading(false);
        }
    }, [viewer, terrainEnabled, terrainLoading]);
    const lastUpdateRef = useRef(0);
    const lastAltRef = useRef(0);
    const toggleExpanded = () => {
        setIsExpanded((prev) => {
            if (!prev)
                eventBus.emit('overlay:opened', { id: 'map-options' });
            return !prev;
        });
    };
    // Close when the peer top-right overlay (Help & Support) opens.
    useEventSubscribe('overlay:opened', (p) => {
        if (p.id !== 'map-options')
            setIsExpanded(false);
    }, []);
    const isViewerReady = useMemo(() => !!viewer && !viewer.isDestroyed(), [viewer]);
    const updateIndicators = useCallback(() => {
        if (!viewer || viewer.isDestroyed())
            return;
        const now = performance.now();
        if (now - lastUpdateRef.current < 50)
            return;
        const alt = viewer.camera.positionCartographic.height;
        const last = lastAltRef.current;
        if (last === 0 || Math.abs(alt - last) / Math.max(1, last) > 0.01) {
            lastAltRef.current = alt;
            setAltitudeMeters(alt);
        }
        lastUpdateRef.current = now;
    }, [viewer]);
    // Track altitude only while the controls tab is open, to avoid work when collapsed.
    useEffect(() => {
        if (!isExpanded || !viewer || viewer.isDestroyed() || activeTab !== 'controls')
            return;
        const camera = viewer.camera;
        const scene = viewer.scene;
        const listener = updateIndicators;
        camera.changed.addEventListener(listener);
        scene.preRender.addEventListener(listener);
        updateIndicators();
        return () => {
            try {
                camera.changed.removeEventListener(listener);
            }
            catch (_e) { }
            try {
                scene.preRender.removeEventListener(listener);
            }
            catch (_e) { }
        };
    }, [isExpanded, activeTab, viewer, updateIndicators]);
    // Continuous (press-and-hold) camera movers. Each takes the elapsed frame time
    // in seconds and moves by speed * dt, so a held button glides smoothly. Pan and
    // zoom distances scale with camera height for consistent feel at any altitude.
    const panHold = useCallback((dir) => (dt) => {
        if (!isViewerReady)
            return;
        const cam = viewer.camera;
        const dist = clamp(cam.positionCartographic.height * PAN_PER_SEC, 50, 200000) * dt;
        try {
            if (dir === 'left')
                cam.moveLeft(dist);
            else if (dir === 'right')
                cam.moveRight(dist);
            else if (dir === 'up')
                cam.moveUp(dist);
            else
                cam.moveDown(dist);
        }
        catch (_e) { }
    }, [isViewerReady, viewer]);
    const zoomHold = useCallback((dir) => (dt) => {
        if (!isViewerReady)
            return;
        const cam = viewer.camera;
        const dist = clamp(cam.positionCartographic.height * ZOOM_PER_SEC, 5, 200000) * dt;
        try {
            if (dir === 'in')
                cam.zoomIn(dist);
            else
                cam.zoomOut(dist);
        }
        catch (_e) { }
    }, [isViewerReady, viewer]);
    const tiltHold = useCallback((dir) => (dt) => {
        if (!isViewerReady)
            return;
        const cam = viewer.camera;
        const amt = TILT_RAD_PER_SEC * dt;
        try {
            if (dir === 'up')
                cam.lookUp(amt);
            else
                cam.lookDown(amt);
        }
        catch (_e) { }
    }, [isViewerReady, viewer]);
    const onTiltRestore = useCallback(() => {
        if (!isViewerReady)
            return;
        try {
            const camera = viewer.camera;
            camera.setView({
                destination: camera.position,
                orientation: {
                    heading: camera.heading,
                    pitch: -Cesium.Math.PI_OVER_TWO,
                    roll: 0
                }
            });
        }
        catch (_e) { }
    }, [isViewerReady, viewer]);
    // Reset view (recenter)
    const onResetView = useCallback(async () => {
        if (!isViewerReady)
            return;
        try {
            const bounds = (selectedProject === null || selectedProject === void 0 ? void 0 : selectedProject.getBounds) ? selectedProject.getBounds() : null;
            if (bounds) {
                const west = bounds.getWest();
                const south = bounds.getSouth();
                const east = bounds.getEast();
                const north = bounds.getNorth();
                try {
                    // Calculate terrain-aware altitude for project bounds
                    const boundsRect = { west, south, east, north };
                    const terrainAwareAltitude = await calculateTerrainAwareAltitude(viewer, boundsRect);
                    // Create destination position above terrain
                    const centerLng = (west + east) / 2;
                    const centerLat = (south + north) / 2;
                    const destination = Cesium.Cartesian3.fromDegrees(centerLng, centerLat, terrainAwareAltitude);
                    viewer.camera.flyTo({ destination, duration: 1.5 });
                }
                catch (terrainError) {
                    console.warn('Failed to calculate terrain-aware altitude for reset view, falling back to rectangle flyTo:', terrainError);
                    // Fallback to original rectangle-based flyTo
                    const rect = Cesium.Rectangle.fromDegrees(west, south, east, north);
                    viewer.camera.flyTo({ destination: rect, duration: 1.5 });
                }
            }
            else {
                // Reset to default view if no project selected
                try {
                    viewer.camera.setView({
                        destination: Cesium.Cartesian3.fromDegrees(-105.6836, 40.3428, 5000000),
                        orientation: {
                            heading: Cesium.Math.toRadians(0.0),
                            pitch: Cesium.Math.toRadians(-90.0),
                            roll: 0.0
                        }
                    });
                }
                catch (_e) { }
            }
        }
        catch (_e) { }
    }, [isViewerReady, viewer, selectedProject]);
    const formattedAltitude = useMemo(() => {
        if (system === 'metric') {
            const fm = formatMetricDistance(altitudeMeters);
            return { text: fm.text, unit: fm.unit };
        }
        const fi = formatImperialDistance(metersToFeet(altitudeMeters));
        return { text: fi.text, unit: fi.unit };
    }, [altitudeMeters, system]);
    return (_jsxs(_Fragment, { children: [_jsx(CoverageHoverTooltip, { hover: coverageHover }), _jsx("div", { className: `absolute ${isExpanded ? 'z-[60]' : 'z-[55]'} top-4 right-4 max-md:top-4 max-md:right-4`, children: _jsxs(MorphPanel, { collapsed: !isExpanded, onToggle: toggleExpanded, icon: _jsx(SlidersHorizontal, { size: 18, style: { color: 'var(--sand)' } }), label: "Map Options", anchor: "top-right", expandedWidth: 380, children: [_jsxs("div", { className: "p-3 border-b flex items-center justify-between cursor-pointer flex-shrink-0", style: { borderColor: 'var(--pewter)' }, onClick: toggleExpanded, children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "font-heading text-sm font-semibold", style: { color: 'var(--sand)' }, children: "Map Options" }), _jsx(SlidersHorizontal, { size: 16, style: { color: 'var(--sand)' } })] }), _jsx(ChevronDown, { size: 16 })] }), _jsxs("div", { className: "p-3", children: [_jsx("div", { className: "mb-3 flex items-center justify-center", children: _jsxs("div", { className: "flex rounded-full overflow-hidden border", style: { borderColor: 'var(--pewter)' }, children: [_jsx("button", { onClick: () => setActiveTab('basemap'), className: `px-3 py-1 text-xs ${activeTab === 'basemap' ? 'font-semibold' : ''}`, style: {
                                                    backgroundColor: activeTab === 'basemap' ? 'var(--sand)' : 'var(--charcoal)',
                                                    color: activeTab === 'basemap' ? 'var(--forest)' : 'var(--dew)'
                                                }, children: "Display" }), _jsx("button", { onClick: () => setActiveTab('controls'), className: `px-3 py-1 text-xs ${activeTab === 'controls' ? 'font-semibold' : ''}`, style: {
                                                    backgroundColor: activeTab === 'controls' ? 'var(--sand)' : 'var(--charcoal)',
                                                    color: activeTab === 'controls' ? 'var(--forest)' : 'var(--dew)'
                                                }, children: "Controls" })] }) }), _jsxs("div", { className: "w-full", style: { minHeight: 300 }, children: [activeTab === 'controls' && (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex flex-row items-stretch gap-0 md:gap-4", children: [_jsxs("div", { className: "relative border rounded-md px-0 md:px-2 pt-5 pb-1 md:pb-3 border-[var(--pewter)] flex flex-col items-center justify-between min-h-[140px] md:min-h-[160px]", style: { flex: '1.6' }, children: [_jsx("div", { className: "absolute -top-3 left-1/2 -translate-x-1/2 px-2 text-[10px] uppercase tracking-wide", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: "Zoom" }), _jsx(ControlButton, { onHold: zoomHold('in'), title: "Zoom in", round: true, children: _jsx(Plus, { size: 16 }) }), _jsx("div", { className: "flex items-center justify-center text-[var(--sand)] opacity-80", children: _jsx(Search, { size: 14 }) }), _jsx(ControlButton, { onHold: zoomHold('out'), title: "Zoom out", round: true, children: _jsx(Minus, { size: 16 }) }), _jsxs("div", { className: "mt-2 w-full flex flex-col items-center justify-center gap-1 min-h-[32px]", children: [_jsx("div", { className: "flex items-center gap-1", style: { color: 'var(--sand)' }, children: _jsxs("span", { className: "text-[10px] font-semibold", children: ["ALT (", formattedAltitude.unit, ")"] }) }), _jsx("div", { className: "text-[10px] font-semibold", style: { color: 'var(--sand)' }, children: formattedAltitude.text })] })] }), _jsxs("div", { className: "relative border rounded-md p-1 md:p-3 pt-5 border-[var(--pewter)] flex flex-col items-center justify-center min-h-[140px] md:min-h-[160px]", style: { flex: '4' }, children: [_jsx("div", { className: "absolute -top-3 left-1/2 -translate-x-1/2 px-2 text-[10px] uppercase tracking-wide", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: "Pan" }), _jsx("div", { className: "p-1 md:p-3 rounded-full border flex items-center justify-center", style: { borderColor: 'var(--pewter)', background: 'linear-gradient(145deg, rgba(24,24,24,0.9), rgba(38,51,40,0.7))' }, children: _jsxs("div", { className: "grid grid-cols-3 gap-1 md:gap-2 items-center", children: [_jsx("div", {}), _jsx(ControlButton, { onHold: panHold('up'), title: "Pan up", round: true, children: _jsx(ArrowUp, { size: 16 }) }), _jsx("div", {}), _jsx(ControlButton, { onHold: panHold('left'), title: "Pan left", round: true, children: _jsx(ArrowLeft, { size: 16 }) }), _jsx(ControlButton, { onAction: onResetView, title: "Reset view", disabled: false, round: true, children: _jsx(Crosshair, { size: 16 }) }), _jsx(ControlButton, { onHold: panHold('right'), title: "Pan right", round: true, children: _jsx(ArrowRight, { size: 16 }) }), _jsx("div", {}), _jsx(ControlButton, { onHold: panHold('down'), title: "Pan down", round: true, children: _jsx(ArrowDown, { size: 16 }) }), _jsx("div", {})] }) })] }), _jsxs("div", { className: "relative border rounded-md p-1 md:p-3 pt-5 border-[var(--pewter)] flex flex-col items-center justify-between min-h-[140px] md:min-h-[160px]", style: { flex: '1.4' }, children: [_jsx("div", { className: "absolute -top-3 left-1/2 -translate-x-1/2 px-2 text-[10px] uppercase tracking-wide", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: "Tilt" }), _jsx(ControlButton, { onHold: tiltHold('up'), title: "Tilt up (toward horizon)", round: true, children: _jsx(ArrowUp, { size: 16 }) }), _jsx("div", { className: "flex items-center justify-center text-[var(--sand)] opacity-80", children: _jsx(MoveVertical, { size: 14 }) }), _jsx(ControlButton, { onHold: tiltHold('down'), title: "Tilt down (toward nadir)", round: true, children: _jsx(ArrowDown, { size: 16 }) }), _jsx(ControlButton, { onAction: onTiltRestore, title: "Restore to top-down", round: true, children: _jsx(RefreshCcw, { size: 16 }) })] })] }), _jsxs("div", { className: "relative border rounded-md p-2 border-[var(--pewter)] h-20 flex items-center justify-center", children: [_jsx("div", { className: "absolute -top-3 left-1/2 -translate-x-1/2 px-2 text-[10px] uppercase tracking-wide", style: { backgroundColor: 'var(--forest)', color: 'var(--sand)' }, children: "Unit" }), _jsx("div", { className: "flex items-center justify-center gap-2", children: _jsxs("div", { className: "flex rounded-full overflow-hidden border", style: { borderColor: 'var(--pewter)' }, children: [_jsx("button", { onClick: () => setSystem('metric'), className: `px-3 py-1 text-[10px] ${system === 'metric' ? 'font-semibold' : ''}`, style: {
                                                                            backgroundColor: system === 'metric' ? 'var(--sand)' : 'var(--charcoal)',
                                                                            color: system === 'metric' ? 'var(--forest)' : 'var(--dew)'
                                                                        }, title: "Use metric units", children: "m" }), _jsx("button", { onClick: () => setSystem('imperial'), className: `px-3 py-1 text-[10px] ${system === 'imperial' ? 'font-semibold' : ''}`, style: {
                                                                            backgroundColor: system === 'imperial' ? 'var(--sand)' : 'var(--charcoal)',
                                                                            color: system === 'imperial' ? 'var(--forest)' : 'var(--dew)'
                                                                        }, title: "Use imperial units", children: "ft" })] }) })] })] })), activeTab === 'basemap' && (_jsxs("div", { className: "grid grid-cols-1 gap-2 w-full", children: [_jsx(SectionDivider, { label: "Map display" }), _jsxs("button", { onClick: onToggleTerrain, disabled: terrainLoading, className: `w-full px-3 py-1.5 text-xs flex items-center justify-center gap-1.5 rounded-full border transition-colors ${terrainEnabled ? 'font-semibold' : ''} ${terrainLoading ? 'opacity-50 cursor-not-allowed' : ''}`, style: {
                                                        borderColor: terrainEnabled ? 'var(--forest)' : 'var(--pewter)',
                                                        backgroundColor: terrainEnabled ? 'var(--sand)' : 'var(--charcoal)',
                                                        color: terrainEnabled ? 'var(--forest)' : 'var(--dew)'
                                                    }, title: terrainEnabled ? 'Disable 3D terrain' : 'Enable 3D terrain', children: [_jsx(Mountain, { size: 14 }), terrainLoading ? 'Loading…' : terrainEnabled ? '3D Terrain: On' : '3D Terrain: Off'] }), _jsx(CoverageControlUI, { visible: coverageVisible, opacity: coverageOpacity, onToggleVisible: () => setCoverageVisible((v) => !v), onOpacityChange: setCoverageOpacity }), _jsx(SectionDivider, { label: "Basemap" }), _jsxs("button", { onClick: () => onChangeBasemap('bing-aerial'), className: "px-3 py-2 text-xs rounded-full border transition-colors text-left flex items-center gap-2", style: {
                                                        backgroundColor: currentBasemap === 'bing-aerial' ? 'var(--sand)' : 'var(--charcoal)',
                                                        color: currentBasemap === 'bing-aerial' ? 'var(--forest)' : 'var(--dew)',
                                                        borderColor: currentBasemap === 'bing-aerial' ? 'var(--forest)' : 'transparent'
                                                    }, children: [_jsx(Satellite, { size: 14 }), "Bing Aerial"] }), _jsxs("button", { onClick: () => onChangeBasemap('bing-aerial-labels'), className: "px-3 py-2 text-xs rounded-full border transition-colors text-left flex items-center gap-2", style: {
                                                        backgroundColor: currentBasemap === 'bing-aerial-labels' ? 'var(--sand)' : 'var(--charcoal)',
                                                        color: currentBasemap === 'bing-aerial-labels' ? 'var(--forest)' : 'var(--dew)',
                                                        borderColor: currentBasemap === 'bing-aerial-labels' ? 'var(--forest)' : 'transparent'
                                                    }, children: [_jsx(Satellite, { size: 14 }), "Bing Aerial (labels)"] }), _jsxs("button", { onClick: () => onChangeBasemap('usgs-topo'), className: "px-3 py-2 text-xs rounded-full border transition-colors text-left flex items-center gap-2", style: {
                                                        backgroundColor: currentBasemap === 'usgs-topo' ? 'var(--sand)' : 'var(--charcoal)',
                                                        color: currentBasemap === 'usgs-topo' ? 'var(--forest)' : 'var(--dew)',
                                                        borderColor: currentBasemap === 'usgs-topo' ? 'var(--forest)' : 'transparent'
                                                    }, children: [_jsx(Map, { size: 14 }), "Topo"] }), _jsxs("button", { onClick: () => onChangeBasemap('carto-light'), className: "px-3 py-2 text-xs rounded-full border transition-colors text-left flex items-center gap-2", style: {
                                                        backgroundColor: currentBasemap === 'carto-light' ? 'var(--sand)' : 'var(--charcoal)',
                                                        color: currentBasemap === 'carto-light' ? 'var(--forest)' : 'var(--dew)',
                                                        borderColor: currentBasemap === 'carto-light' ? 'var(--forest)' : 'transparent'
                                                    }, children: [_jsx(LocateFixed, { size: 14 }), "Light"] }), _jsxs("button", { onClick: () => onChangeBasemap('carto-dark'), className: "px-3 py-2 text-xs rounded-full border transition-colors text-left flex items-center gap-2", style: {
                                                        backgroundColor: currentBasemap === 'carto-dark' ? 'var(--sand)' : 'var(--charcoal)',
                                                        color: currentBasemap === 'carto-dark' ? 'var(--forest)' : 'var(--dew)',
                                                        borderColor: currentBasemap === 'carto-dark' ? 'var(--forest)' : 'transparent'
                                                    }, children: [_jsx(Locate, { size: 14 }), "Dark"] })] }))] })] })] }) })] }));
};
